// @flow
import * as Sentry from '@sentry/react'
import zentakeApi from 'api'
import * as pdfFillerApi from 'api/pdfFillerApi'
import {
  createAnswers,
  getPdfDocument,
  getResponse,
  submitResponse,
  updateMessage,
} from 'api/pdfFillerApi'
import BuilderNavBar from 'components/BuilderNavBar'
import Loading from 'components/Loading'
import ModalFormSecurity from 'components/Modal/ModalFormSecurity'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import {
  saveExtraContexts,
  validateExtraContexts,
} from 'pages/Utils/extra-contexts'
import { useUpdateResponseQueue } from 'queries/response_queue/useUpdateResponseQueue'
import * as React from 'react'
import { Prompt, withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { Answer } from 'types/Answer'
import type { AnswerSignature } from 'types/answer_signature'
import type { InputField } from 'types/input-field'
import type { PdfDocumentApiResponse } from 'types/pdf-document-api-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

import useSearchParams from '@/hooks/useSearchParams'
import cn from '@/utils/tailwind/cn'

import DocumentSectionFooter from './DocumentSectionFooter'
import PDFFillerRouter from './PDFFillerRouter'
import { Sidebar } from './Sidebar'

type Props = {
  hideFooter?: boolean,
  isPacket?: boolean,
  preloadedResponse?: ?Response,
}

type InjectedProps = {
  history: {
    push: (path: string) => void,
    location: {
      pathname: string,
      search: string,
    },
  },
}

function DocumentSection({
  history,
  hideFooter,
  isPacket,
  preloadedResponse,
}: Props & InjectedProps): React.Node {
  const [isSaving, setIsSaving] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [isLinkExpired, setIsLinkExpired] = React.useState(false)
  const [hasNoResponse, setHasNoResponse] = React.useState(false)
  const { updateResponseQueue, isLoading: isUpdateResponseQueueLoading } =
    useUpdateResponseQueue()
  const searchParams = useSearchParams()

  const {
    setSubmitFunctions,
    teamConfig,
    setTeamConfig,
    setResponseSubmitted,
  } = React.useContext(ZentakeContext)

  const {
    completePage,
    answers,
    autoSignOffEnabled,
    dirty,
    documentDownloadable,
    documentName,
    elationPdfNonFax,
    extraContexts,
    inputFields,
    inputRefs,
    internalForm,
    isKiosk,
    isSubmitted,
    isTablet,
    notificationUsers,
    pages,
    pdfReportType,
    removedInputFields,
    resourceId,
    setAnswers,
    setAutoSignOffEnabled,
    setDirty,
    setDocumentDownloadable,
    setDocumentName,
    setElationPdfNonFax,
    setError,
    setErrors,
    setInputFields,
    setInternalForm,
    setIsSubmitted,
    setNotificationUsers,
    setPageRefs,
    setPages,
    setPdfReportType,
    setRedirectUrl,
    setCompletePage,
    setResourceId,
    setSignatureAnswers,
    setSubmitting,
    setTeamDetails,
    signatureAnswers,
    viewMode,
  } = React.useContext(PDFFillerContext)

  const [currentRefIndex, setCurrentRefIndex] = React.useState(null)
  const source = React.useMemo(() => {
    if (isTablet) return 'tablet'

    if (isKiosk) return 'kiosk'

    return viewMode
  }, [isKiosk, isTablet, viewMode])

  const requiredInputs = React.useMemo(() => {
    return Object.keys(inputFields)
      .map((key) => inputFields[key])
      .filter(
        (input) =>
          input.required && input.is_office_use === (viewMode === 'office-use')
      )
  }, [inputFields, viewMode])

  const missingInputs = React.useMemo(() => {
    return requiredInputs.filter(
      (input) =>
        !answers[input.id]?.answer_raw &&
        !signatureAnswers[input.linked_signature ?? '']?.created_at &&
        !signatureAnswers[input.id]?.text &&
        !signatureAnswers[input.id]?.image
    )
  }, [answers, requiredInputs, signatureAnswers])

  const isMissingInputs = React.useMemo(() => {
    return missingInputs.length > 0
  }, [missingInputs?.length])

  const setDocument = React.useCallback(
    (document: PdfDocumentApiResponse) => {
      const inputFieldsObject = document.input_fields.reduce(
        (acc: { [string]: InputField }, inputField) => {
          acc[inputField.id] = inputField
          return acc
        },
        {}
      )

      const pageRefsObject = document.pages.reduce(
        (acc: { [string]: { current: HTMLDivElement | null } }, page) => {
          acc[page.id] = React.createRef()
          return acc
        },
        {}
      )

      setPages(document.pages)
      setPageRefs(pageRefsObject)
      setInputFields(inputFieldsObject)
      setDocumentName(document.name)
      setDocumentDownloadable(document.downloadable)
      setRedirectUrl(document.redirect_url)
      setCompletePage({
        redirectUrl: document?.redirect_url,
        customCompletePage: document?.custom_complete_page,
        hasRedirectRules: document?.has_redirect_rules,
      })
      setPdfReportType(document.pdf_report_type)
      setDirty(false)
      setTeamDetails(document?.team)
      setNotificationUsers(document?.notification_users || [])
      setInternalForm(document?.is_internal_form)
      setAutoSignOffEnabled(document?.auto_sign_off)
      setElationPdfNonFax(document?.elation_pdf_non_fax)

      if (!teamConfig) {
        setTeamConfig(document?.team?.config)
      }
    },
    [
      setPages,
      setPageRefs,
      setInputFields,
      setDocumentName,
      setDocumentDownloadable,
      setRedirectUrl,
      setCompletePage,
      setPdfReportType,
      setDirty,
      setTeamDetails,
      setNotificationUsers,
      setInternalForm,
      setAutoSignOffEnabled,
      setElationPdfNonFax,
      teamConfig,
      setTeamConfig,
    ]
  )

  const fetchDocument = React.useCallback(async () => {
    if (!resourceId) return

    setLoading(true)
    const response = await getPdfDocument(resourceId)
    setDocument(response)
    setLoading(false)
  }, [resourceId, setDocument])

  const _fetchResponse = React.useCallback(async () => {
    if (preloadedResponse) {
      return preloadedResponse
    }

    try {
      setLoading(true)

      return await getResponse(resourceId)
    } catch (error) {
      if (error?.message === 'Message code is invalid or expired.') {
        try {
          // eslint-disable-next-line import/no-named-as-default-member
          await zentakeApi.resendMessageCodeLink(resourceId)
        } finally {
          setIsLinkExpired(true)
        }
        return
      }

      setHasNoResponse(true)
    } finally {
      setLoading(false)
    }
  }, [preloadedResponse, resourceId])

  const fetchResponse = React.useCallback(async () => {
    if (!resourceId) return

    const response = await _fetchResponse()

    if (!response) return

    setResourceId(response?.uid || response?.id)

    if (!response.document) return

    const { document } = response

    const snapshot: null | PdfDocumentApiResponse = response.document_snapshot
      ? JSON.parse(response.document_snapshot)
      : null

    setDocument(snapshot || document)
    setIsSubmitted(response.is_submitted)

    const answersObject =
      response.answers?.reduce((acc: { [string]: Answer }, currentValue) => {
        if (!currentValue.input_field) return acc

        acc[currentValue.input_field] = currentValue

        return acc
      }, {}) ?? {}

    setAnswers(answersObject)

    const signatureAnswersObject =
      response.answer_signatures?.reduce(
        (acc: { [string]: AnswerSignature }, currentValue) => {
          if (!currentValue.input_field) return acc

          acc[currentValue.input_field] = currentValue

          return acc
        },
        {}
      ) ?? {}

    setSignatureAnswers(signatureAnswersObject)
  }, [
    _fetchResponse,
    resourceId,
    setAnswers,
    setDocument,
    setIsSubmitted,
    setResourceId,
    setSignatureAnswers,
  ])

  React.useEffect(() => {
    if (['fill', 'response', 'office-use'].includes(viewMode)) {
      fetchResponse()

      return
    }
    fetchDocument()
  }, [viewMode, fetchDocument, fetchResponse])

  const validate = React.useCallback(() => {
    if (!documentName) {
      return 'Name is required'
    }

    return null
  }, [documentName])

  const handleSave = React.useCallback(async () => {
    const errorMessage = validate()

    if (errorMessage) {
      setError(errorMessage)
      return
    }

    await validateExtraContexts(extraContexts)
    await saveExtraContexts(extraContexts)

    const data = {
      name: documentName,
      downloadable: documentDownloadable,
      pdf_report_type: pdfReportType,
      removed_input_fields: removedInputFields,
      notification_users: notificationUsers,
      input_fields: Object.keys(inputFields).map((key) => inputFields[key]),
      is_internal_form: internalForm,
      auto_sign_off: autoSignOffEnabled,
      elation_pdf_non_fax: elationPdfNonFax,
      redirect_url: completePage?.redirectUrl || null,
      has_redirect_rules: completePage?.hasRedirectRules,
    }

    setIsSaving(true)

    try {
      await pdfFillerApi.updatePdfDocument(resourceId, data)
      toast.success('Document saved')
      setDirty(false)
    } catch (error) {
      toast.error('Error saving document')
    } finally {
      setIsSaving(false)
    }
  }, [
    completePage,
    autoSignOffEnabled,
    documentDownloadable,
    documentName,
    elationPdfNonFax,
    extraContexts,
    inputFields,
    internalForm,
    notificationUsers,
    pdfReportType,
    removedInputFields,
    resourceId,
    setDirty,
    setError,
    validate,
  ])

  const orderedInputRefs = React.useMemo(
    () =>
      inputRefs.sort((a, b) => {
        const aBottom = a.current?.getBoundingClientRect().bottom || 0
        const bBottom = b.current?.getBoundingClientRect().bottom || 0

        const aLeft = a.current?.getBoundingClientRect().left || 0
        const bLeft = b.current?.getBoundingClientRect().left || 0

        if (aBottom === bBottom) {
          return aLeft - bLeft
        }

        return aBottom - bBottom
      }),
    [inputRefs]
  )

  const onNextClick = React.useCallback(() => {
    const newRefIndex =
      currentRefIndex !== null
        ? Math.min(currentRefIndex + 1, orderedInputRefs.length - 1)
        : 0

    setCurrentRefIndex(newRefIndex)
    const inputRef = orderedInputRefs[newRefIndex]
    if (inputRef) {
      inputRef.current?.focus()
    }
  }, [currentRefIndex, orderedInputRefs])

  const onPreviousClick = React.useCallback(() => {
    const newRefIndex =
      currentRefIndex !== null ? Math.max(currentRefIndex - 1, 0) : 0

    setCurrentRefIndex(newRefIndex)
    const inputRef = orderedInputRefs[newRefIndex]
    if (inputRef) {
      inputRef.current?.focus()
    }
  }, [currentRefIndex, orderedInputRefs])

  const handlePreview = React.useCallback(() => {
    window.open(`/r/preview/${resourceId}`)
  }, [resourceId])

  React.useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty || (viewMode === 'fill' && !isSubmitted)) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
      return undefined
    }

    window.addEventListener('beforeunload', beforeUnload)

    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [dirty, isMissingInputs, isSubmitted, viewMode])

  const validateDocument = React.useCallback(() => {
    setErrors(
      missingInputs.reduce((acc: { [string]: string }, currentValue) => {
        acc[currentValue.id] = 'This field is required'
        return acc
      }, {})
    )

    if (missingInputs.length === 0) return true

    try {
      const firstMissingInput = missingInputs[0]
      const missingInputElement = document.getElementById(firstMissingInput.id)

      missingInputElement?.scrollIntoView()
    } catch {
      // do nothing
    }

    return false
  }, [missingInputs, setErrors])

  const sendMessageAndRedirect = React.useCallback(async (): Promise<void> => {
    const urlParams = new URLSearchParams(history.location.search)
    const messageId = urlParams.get('messageId')

    if (!messageId) return

    await updateMessage(messageId, { is_waiting: false })

    history.push('/')

    toast.info(`Document Sent`)
  }, [history])

  const handleOfficeUseSubmit = React.useCallback(async () => {
    if (!isSubmitted) {
      await sendMessageAndRedirect()

      return
    }

    history.push(`/response/${resourceId}`)
    toast.success('Saved')
  }, [history, isSubmitted, resourceId, sendMessageAndRedirect])

  const handleSubmit = React.useCallback(
    async (
      config: ?{
        validate: boolean,
        submit?: boolean,
        send?: boolean,
        lockInternalForm?: boolean,
      } = {
        validate: true,
        submit: false,
        send: true,
        lockInternalForm: false,
      }
    ): Promise<?Response> => {
      const validate = config?.validate

      if (!resourceId) return

      if (validate && !validateDocument()) {
        toast.error('Please fill out all required fields.')
        return
      }

      setSubmitting(true)

      const payload = {
        answers: Object.keys(answers)
          .map((key) => ({ ...answers[key] }))
          .filter((answer) => answer.answer_raw),
        submit: config?.submit || false,
        response: resourceId || null,
        lock_internal_form: config?.lockInternalForm || false,
      }

      try {
        const result = await createAnswers(payload, source)

        if (!isPacket) {
          if (viewMode === 'office-use') {
            if (config?.send) {
              await handleOfficeUseSubmit()
            }
          } else {
            await submitResponse(resourceId, source)
            setResponseSubmitted(true)
          }
        }

        setIsSubmitted(true)

        return result
      } catch (e) {
        Sentry.captureException(e, {
          extra: { payload: JSON.stringify(payload) },
        })

        toast.error('Something went wrong. Please try again later.')
      } finally {
        setSubmitting(false)
      }
    },
    [
      answers,
      handleOfficeUseSubmit,
      isPacket,
      resourceId,
      setIsSubmitted,
      setResponseSubmitted,
      setSubmitting,
      source,
      validateDocument,
      viewMode,
    ]
  )

  const handleSendToQueue = React.useCallback(async () => {
    const saved = await handleSubmit({
      validate: true,
      submit: false,
      send: false,
    })
    if (saved) {
      setSubmitting(true)
      await updateResponseQueue({
        is_packet: false,
        is_queued: true,
        response_uid: resourceId,
      })
      setSubmitting(false)
      toast.success('Added to queue')
      history.push('/')
    }
  }, [handleSubmit, history, resourceId, setSubmitting, updateResponseQueue])

  React.useEffect(() => {
    setSubmitFunctions((submitFunctions) => ({
      ...submitFunctions,
      document: handleSubmit,
    }))
  }, [handleSubmit, setSubmitFunctions])

  if (isLinkExpired) {
    return (
      <ModalFormSecurity
        hideButton
        isVisible
        description={
          <>
            Protecting your data is our top priority.{' '}
            <u>This link has expired.</u> To keep your information secure, we
            have sent you a new link.
          </>
        }
        title="For your security, We’ve sent you a new link"
      />
    )
  }

  if (hasNoResponse) {
    return (
      <ModalFormUnavailable
        hideButton
        isVisible
        description="Sorry, this form is not available anymore"
        title="Form not available"
      />
    )
  }

  if (!pages.length) return null

  const isFilling = !['edit', 'response', 'preview'].includes(viewMode)

  if (loading || isUpdateResponseQueueLoading) return <Loading />

  const isDocumentSubmittable = viewMode === 'office-use' ? true : !isSubmitted

  const showFooter =
    isFilling && !hideFooter && isDocumentSubmittable && viewMode

  const isFormsPage = searchParams.get('response_type') === 'form'
  const isSettingsPage = history.location.pathname.includes('settings')

  const isTabletModePacket = history?.location?.pathname?.includes('/t/p/')
  const isTabletModeForms = history?.location?.pathname?.includes('/t/f/')
  const isFillResponse = history?.location?.pathname?.includes('/r/')
  const isDocumentPreview = history?.location?.pathname?.includes('/r/preview/')
  const isDocumentBuilder = history?.location?.pathname?.includes('/pdf/')
  const isInternalForm =
    history?.location?.pathname?.includes('/internal-form/')

  const packetOfficeUse = history.location.pathname.includes(
    'fillPacketOfficeUseQuestions'
  )

  const innerContainerStyles = {
    'h-[calc(100svh-62px)]': isFormsPage,
    'overflow-hidden h-full':
      isDocumentPreview ||
      isTabletModeForms ||
      isFillResponse ||
      isTabletModePacket ||
      isInternalForm,
  }

  const mainContainerStyles = {
    'h-[calc(100svh-288px)]':
      !isPacket && !isTabletModeForms && !internalForm && !isDocumentBuilder,
    'h-[calc(100svh-204px)]': packetOfficeUse,
    'h-auto':
      isDocumentPreview ||
      isTabletModeForms ||
      isFillResponse ||
      isTabletModePacket ||
      internalForm,
  }

  return (
    <>
      <Prompt
        message={() => 'Changes you made may not be saved.'}
        when={dirty}
      />
      <div
        className={cn(
          'flex h-[calc(100svh-65px)] w-full flex-col overflow-hidden',
          mainContainerStyles
        )}
      >
        {viewMode === 'edit' && (
          <BuilderNavBar
            handlePreview={handlePreview}
            handleSave={handleSave}
            previewDisabled={dirty}
            resourceType="document"
            saveLoading={isSaving || isUpdateResponseQueueLoading}
            settingsActive={isSettingsPage}
          />
        )}
        <div
          className={cn('flex w-full gap-4', {
            'grid w-full grid-cols-[400px_1fr]': isSettingsPage,
          })}
        >
          {viewMode === 'edit' && (
            <Sidebar className="h-[calc(100svh-120px)] min-h-[calc(100svh-120px)] !overflow-auto" />
          )}
          <div
            className={cn(
              'h-[calc(calc(100svh-120px))] w-full overflow-y-scroll',
              innerContainerStyles
            )}
          >
            <PDFFillerRouter />
          </div>
        </div>
      </div>

      {showFooter && (
        <DocumentSectionFooter
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
          onSendQueue={handleSendToQueue}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}

const EnhancedDocumentSection: React.ComponentType<Props> =
  withRouter(DocumentSection)

export default EnhancedDocumentSection
